import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { observer } from "mobx-react";
import { inject } from 'mobx-react';
import QuantityIndicator from "./QuantityIndicator";
import AddToCart from './AddToCart';
import ProductImage from './ProductImage';
import MobilePrice from './mobile/MobilePrice';
import MobilePriceInfo from './mobile/MobilePriceInfo';
import MobileBulkSalePrice from './mobile/MobileBulkSalePrice';
import { getPriceDetails, getPerUnit } from '@/utils/misc';

const CrossSellView = inject('wooCommerceStore')(observer(({
  product,
  inCart,
  img,
  handleClick,
  selectedVariation,
  setSelectedVariation,
  handleRemoveFromCart,
  handleAddToCart,
  handleClearFromCart,
  handleUpdateQuantity,
  productWeightQuantity,
  quantity,
  handleVariationChange,
  studioStore,
  wooCommerceStore,
}) => {
  const [isImageHovered, setIsImageHovered] = useState(false);
  const t = useTranslations('Product');
  const tu = useTranslations('Units');
  const weightUnit = wooCommerceStore.weightUnit || 'kg';
  const primaryColor = studioStore?.settings?.colors?.primary_color;

  // Memoize price details calculation
  const priceDetails = useMemo(() => {
    const currentProduct = selectedVariation || product;
    const productData = {
      id: currentProduct?.id,
      regular_price: currentProduct?.regular_price,
      sale_price: currentProduct?.sale_price,
      weight: currentProduct?.weight,
      weightable: currentProduct?.weightable,
      units_based: currentProduct?.units_based,
      pack_based: currentProduct?.pack_based,
      short_description: currentProduct?.short_description,
      name: currentProduct?.name,
      milliliter: currentProduct?.milliliter,
      show_price_per_100g: currentProduct?.show_price_per_100g,
    };

    const details = getPriceDetails(productData, weightUnit, tu, product?.type);
    details.variant = "mobile";
    details.isWeightBased = currentProduct?.weightable && currentProduct?.weight > 0 && currentProduct?.regular_price > 0;
    details.isTypeBased = !currentProduct?.weightable && (currentProduct?.units_based || currentProduct?.pack_based);
    return details;
  }, [selectedVariation, product, weightUnit, tu]);

  // Memoize the product display type calculation
  const productDisplayType = useMemo(() => {
    const currentProduct = selectedVariation || product;
    return getPerUnit({
      perUnit: currentProduct?.per_unit,
      weightUnit,
      tu,
    });
  }, [selectedVariation, product, weightUnit, tu]);

  return (
    <>
      <header className="relative text-center">
        <div className="bg-yellow-400 text-xs p-1 pe-8">
          {t('crosssell_title')}
        </div>
        <div
          className="relative w-auto pt-2 px-2 pe-8"
          onMouseEnter={() => setIsImageHovered(true)}
          onMouseLeave={() => setIsImageHovered(false)}
        >
          <div className="relative w-full">
            <ProductImage img={img} productName={product?.name} />
            <AnimatePresence>
              {isImageHovered && (
                <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                {/* Background with opacity */}
                <div
                  className={`absolute stamp-border stamp-border-md inset-0 z-[-1] ${!primaryColor && 'bg-gray-500'}`}
                  style={{
                    backgroundImage: `radial-gradient(7px, rgba(255, 255, 255, 0) 98%, ${primaryColor}), linear-gradient(${primaryColor} 0 0)`,
                    opacity: 0.50,
                  }}
                  />
                  {/* Content without opacity */}
                  <div className="relative z-10">
                    <AddToCart
                      product={product}
                      handleRemoveFromCart={handleRemoveFromCart}
                      handleAddToCart={handleAddToCart}
                      handleUpdateQuantity={handleUpdateQuantity}
                      productWeightQuantity={productWeightQuantity}
                      quantity={quantity}
                      handleVariationChange={handleVariationChange}
                      productType={product.type}
                      selectedVariation={selectedVariation}
                      setSelectedVariation={setSelectedVariation}
                      productDisplayType={productDisplayType}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className={`product-info flex flex-col flex-1 px-2 md:p-3 md:p-5 cursor-pointer w-full mx-0 md:text-center`}>
          <div className="flex flex-col md:flex-col justify-between">
            <div onClick={handleClick}>
              <h3 className="text-base leading-none text-lg font-bold text-heading cursor-pointer font-secondary text-primary-studio">
                {product.name}
              </h3>
              <hr className="dotted-spaced hidden md:block my-3" />
              <MobilePrice priceDetails={priceDetails} productDisplayType={productDisplayType} />
            </div>
          </div>
          <div className="flex flex-col md:flex-col justify-between">
            <div onClick={handleClick}>
              <MobilePriceInfo priceDetails={priceDetails} />
              <MobileBulkSalePrice
                id={(selectedVariation || product)?.id}
                regularPrice={(selectedVariation || product)?.regular_price}
                bulkDiscountRules={(selectedVariation || product)?.bulk_discount_rules}
              />
            </div>
          </div>
        </div>
        {!!inCart && (
          <div className="absolute bottom-0 right-0 rtl:left-0 rtl:right-auto md:bottom-0 md:top-auto md:right-0 md:rtl:left-0 md:rtl:right-auto p-3 md:p-4">
            <QuantityIndicator inCart={inCart} handleClearFromCart={handleClearFromCart} />
          </div>
        )}
      </header>
    </>
  );
}));

export default CrossSellView;
